import { useCallback, useEffect, useState } from "react";
import { useApi, useHeyApi } from "@platform/api";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  transformBenefitsForSave,
  type LocCoverageType,
} from "@platform/btx-plan-designer";
import {
  proposalQueryKeys,
  type CopiedPlanDetails,
  type PadFillTarget,
  type PlaceholderMaterializeTarget,
  type Proposal,
  type ProposalRateSchedule,
} from "@platform/mpx-quotes-grid";
import {
  replaceProposalRateSchedules,
  type RateScheduleInput,
} from "@platform/btx-rates";
import { useDialogs } from "@platform/hooks";

import useToastNotificationService from "../../../../../../../hooks/useToastNotificationService";

import { AddPlanToGroupRequest } from "./useAddPlanToGroupMutation";
import { copyProposalPlanFieldOverrides } from "./useQuotesFieldOverrides";

import type { RfpBenefitPlan } from "@platform/btx-plan-designer";

/**
 * The plan behind "Delete Plan" isn't always a real proposal_plan row: a
 * placeholder column (awaiting quote) and a pad slot (empty position on an
 * otherwise real, under-quoted group — CD-1612) are both synthesized at read
 * time. `kind` routes the dialog to the right endpoint, mirroring how the
 * edit/save path picks between materialize / add-plan-to-group / PATCH
 * (see useProposalPlanBenefitsMutation).
 */
export type DeletePlanInfo = (
  | { kind: "real"; id: string }
  | { kind: "placeholder"; target: PlaceholderMaterializeTarget }
  | { kind: "pad"; target: PadFillTarget }
) & {
  name: string;
  groupName: string;
};

type usePlanMenuOptionsParams = {
  ecoRfpId: string;
  coverageType?: LocCoverageType;
};

// Strips a rate schedule's DB-generated fields (id, timestamps, parent ids,
// position) down to the RateScheduleInput the replace endpoint accepts. The
// enum-typed fields (rateDesign, coverageTierCode, ...) resolve to a different
// generated package than RateScheduleInput's, so cast through unknown as the
// proposalToRates adapter does — the runtime string values are identical.
const toRateScheduleInput = (
  schedule: ProposalRateSchedule,
): RateScheduleInput =>
  ({
    rateDesign: schedule.rateDesign,
    numberOfLives: schedule.numberOfLives,
    pepm: schedule.pepm,
    rateCoverageIndicator: schedule.rateCoverageIndicator,
    rates: (schedule.rates ?? []).map((rate) => ({
      ageBandLower: rate.ageBandLower,
      ageBandUpper: rate.ageBandUpper,
      coverageTierCode: rate.coverageTierCode,
      numberOfLives: rate.numberOfLives,
      gender: rate.gender,
      isTobaccoRated: rate.isTobaccoRated,
      rate: rate.rate,
      volume: rate.volume,
      unit: rate.unit,
    })),
  }) as unknown as RateScheduleInput;

type usePlanMenuOptionsResult = {
  clipboard: CopiedPlanDetails | null;
  copyPlan: (plan: CopiedPlanDetails, id: string) => void;
  pastePlan: (
    proposalPlanGroupId: string,
    targetGroupCurrentSize: number,
  ) => void;
  openDeletePlanDialog: (data: DeletePlanInfo) => void;
};

export const usePlanMenuOptions = ({
  ecoRfpId,
  coverageType,
}: usePlanMenuOptionsParams): usePlanMenuOptionsResult => {
  const axiosInstance = useApi();
  const { centroPortalClient } = useHeyApi();
  const queryClient = useQueryClient();
  const queryKey = proposalQueryKeys.all(ecoRfpId);
  const { openDialog } = useDialogs();
  const { createToastNotification } = useToastNotificationService();

  const [clipboard, setClipboard] = useState<CopiedPlanDetails | null>(null);

  useEffect(() => {
    setClipboard(null);
  }, [coverageType]);

  // The Rates table and Summary are backed by rate schedules — a separate entity
  // the add-plan endpoint doesn't carry — and there's no GET for them, so the
  // source plan's schedules are read out of the loaded proposals cache by id
  // (matching every scope under the `all` prefix) to replay onto the pasted plan.
  const findSourceRateSchedules = useCallback(
    (sourcePlanId: string): RateScheduleInput[] => {
      if (!sourcePlanId) return [];

      return (
        queryClient
          .getQueriesData<Proposal[]>({ queryKey })
          .flatMap(([, proposals]) => proposals ?? [])
          .flatMap((proposal) => proposal.coverages)
          .flatMap((coverage) => coverage.planGroups)
          .flatMap((group) => group.plans)
          .find((plan) => plan.id === sourcePlanId)
          ?.rateSchedules?.map(toRateScheduleInput) ?? []
      );
    },
    [queryClient, queryKey],
  );

  const pastePlanToGroup = useMutation({
    mutationFn: async ({
      id,
      position,
      clipboard,
    }: {
      id: string;
      position: number;
      clipboard: CopiedPlanDetails;
    }) => {
      const benefits: RfpBenefitPlan["benefits"] = coverageType
        ? (transformBenefitsForSave(
            clipboard.benefits,
            coverageType,
          ) as RfpBenefitPlan["benefits"])
        : clipboard.benefits;
      const body: AddPlanToGroupRequest = {
        position,
        benefits,
        type: clipboard.type,
        optionName: clipboard.optionName,
        fundingType: clipboard.fundingType,
        participationRequirements: clipboard.participationRequirements,
        rateGuarantee: clipboard.rateGuarantee,
        rateCap: clipboard.rateCap,
        eligibilityClasses: clipboard.eligibilityClasses,
      };
      const { data } = await axiosInstance.post<{ id: string }>(
        `btx/v1/proposal-plan-groups/${id}/plans`,
        body,
      );

      // Replay the source plan's rate schedules onto the newly created plan so the
      // Rates table and Summary come across with the rest of the plan.
      const rateSchedules = findSourceRateSchedules(clipboard.sourcePlanId);
      if (rateSchedules.length > 0) {
        await replaceProposalRateSchedules(
          data.id,
          rateSchedules,
          axiosInstance,
        );
      }

      // "Enter Custom Text" values live in the field-overrides store, keyed to
      // the source plan's id, not in plan.benefits — replay them onto the new
      // plan the same way rate schedules are replayed above.
      if (clipboard.sourcePlanId) {
        await copyProposalPlanFieldOverrides({
          centroPortalClient,
          rfpId: ecoRfpId,
          sourceProposalPlanId: clipboard.sourcePlanId,
          targetProposalPlanId: data.id,
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      createToastNotification({
        severity: "success",
        title: "Plan Pasted",
        message: (
          <>
            Plan: <strong>{clipboard?.planLabel}</strong> has been successfully
            pasted to <strong>{clipboard?.carrierName}</strong> Plan Group
          </>
        ),
        autoHideDuration: 1500,
      });
      setClipboard(null);
    },
    onError: (err: Error) =>
      createToastNotification({ severity: "error", message: err.message }),
  });

  // The Quotes grid rebuilds every column when these callbacks change
  // identity, so they depend on the mutation's stable `mutate` rather than on
  // the mutation result, which is a new object every render.
  const { mutate: pastePlanToGroupMutate } = pastePlanToGroup;
  const pastePlan = useCallback(
    (proposalPlanGroupId: string, targetGroupCurrentSize: number) => {
      if (!clipboard) return;
      pastePlanToGroupMutate({
        id: proposalPlanGroupId,
        position: targetGroupCurrentSize,
        clipboard,
      });
    },
    [clipboard, pastePlanToGroupMutate],
  );

  const copyPlan = useCallback(
    (plan: CopiedPlanDetails, id: string): void => {
      setClipboard(plan);
      createToastNotification({
        severity: "success",
        message: `Plan ID: ${id} Copied`,
        autoHideDuration: 1500,
      });
    },
    [createToastNotification],
  );

  const openDeletePlanDialog = useCallback(
    (data: DeletePlanInfo) => openDialog("deletePlan", data),
    [openDialog],
  );

  return {
    copyPlan,
    pastePlan,
    clipboard,
    openDeletePlanDialog,
  };
};
